/**
 * Elegir UNA opción entre pocas, con todas a la vista.
 *
 * POR QUÉ NO UN `<select>`. Con dos o tres opciones cortas —masculino o
 * femenino; cédula, DIMEX o pasaporte— el desplegable es peor por tres
 * razones concretas, todas visibles en un teléfono:
 *
 *  1. HAY QUE ABRIRLO PARA SABER QUÉ HAY. Son dos toques para algo que cabe
 *     entero en una línea, y hasta el primer toque nadie sabe si sus opciones
 *     están ahí.
 *  2. LA LISTA QUE SE ABRE NO ES NUESTRA. La dibuja el sistema operativo: no
 *     respeta el tema oscuro, no respeta el tamaño de letra de la pantalla, y
 *     en una pantalla chica tapa lo que hay alrededor.
 *  3. EL BLANCO NO SE DISTINGUE DE UNA ELECCIÓN. «Elegí una opción» se lee
 *     casi igual que una opción elegida, y por eso alguien manda el
 *     formulario creyendo que ya respondió.
 *
 * Con botones de radio las opciones están a la vista, se elige de un toque, y
 * «todavía no elegí» es evidente porque no hay ninguno marcado.
 *
 * SON RADIOS DE VERDAD, no divs con `onClick`. Eso da gratis lo que a mano
 * casi nunca se hace bien: las flechas del teclado recorren el grupo, un
 * lector de pantalla anuncia «opción 2 de 3», y el `<fieldset>` con su
 * `<legend>` le dice a quién pertenecen las opciones.
 */
import type { ReactNode } from 'react';

export interface Choice<T extends string> {
  value: T;
  label: string;
  /** Una línea corta debajo del texto, cuando la opción necesita explicarse. */
  hint?: string;
}

interface ChoiceGroupProps<T extends string> {
  /** Prefijo de los ids. Cada opción usa `${id}-${value}`. */
  id: string;
  legend: string;
  help?: string | undefined;
  error?: string | undefined;
  /** Texto «obligatorio» / «opcional», junto al título. */
  hint?: string | undefined;
  /** `null` = todavía no eligió. Ninguna opción queda marcada. */
  value: T | null;
  options: ReadonlyArray<Choice<T>>;
  onChange: (value: T) => void;
  disabled?: boolean;
}

export function ChoiceGroup<T extends string>({
  id,
  legend,
  help,
  error,
  hint,
  value,
  options,
  onChange,
  disabled = false,
}: ChoiceGroupProps<T>): JSX.Element {
  const helpId = help === undefined ? undefined : `${id}-help`;
  const errorId = error === undefined ? undefined : `${id}-error`;
  const describedBy = [helpId, errorId].filter((x): x is string => x !== undefined).join(' ');

  return (
    <fieldset
      className={error === undefined ? 'choice-group' : 'choice-group invalid'}
      aria-describedby={describedBy === '' ? undefined : describedBy}
      aria-invalid={error === undefined ? undefined : true}
    >
      <legend>
        {legend}
        {hint !== undefined && <span className="help"> ({hint})</span>}
      </legend>

      {help !== undefined && (
        <span className="help" id={helpId}>
          {help}
        </span>
      )}

      <div className="choice-options">
        {options.map((option) => (
          <label className="choice" key={option.value} htmlFor={`${id}-${option.value}`}>
            <input
              type="radio"
              id={`${id}-${option.value}`}
              // El mismo `name` para todo el grupo: es lo que hace que sean
              // excluyentes y que las flechas del teclado los recorran.
              name={id}
              value={option.value}
              checked={value === option.value}
              disabled={disabled}
              onChange={() => onChange(option.value)}
            />
            <span className="choice-text">
              <span className="choice-label">{option.label}</span>
              {option.hint !== undefined && <span className="help">{option.hint}</span>}
            </span>
          </label>
        ))}
      </div>

      {error !== undefined && (
        <span className="error" id={errorId} role="alert">
          {error}
        </span>
      )}
    </fieldset>
  );
}

/** Envoltorio para cuando el grupo va dentro de otro `fieldset` con leyenda. */
export function ChoiceRow({ children }: { children: ReactNode }): JSX.Element {
  return <div className="choice-options">{children}</div>;
}
